// Page de succès de paiement Stripe
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, polling
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;
  const pollInterval = 2000;

  const pollPaymentStatus = useCallback(async (currentPollCount = 0) => {
    if (currentPollCount >= maxPolls) {
      setStatus('error');
      toast.error('Impossible de vérifier le paiement. Contactez le support.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la vérification');
      }
      
      const data = await response.json();
      setPaymentData(data);
      
      if (data.is_success || data.payment_status === 'paid' || data.payment_status === 'free_trial') {
        setStatus('success');
        toast.success('Paiement confirmé !');
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('Session de paiement expirée');
      } else {
        // Continue polling
        setStatus('polling');
        setPollCount(currentPollCount + 1);
        setTimeout(() => pollPaymentStatus(currentPollCount + 1), pollInterval);
      }
    } catch (error) {
      console.error('Poll error:', error);
      if (currentPollCount < maxPolls - 1) {
        setPollCount(currentPollCount + 1);
        setTimeout(() => pollPaymentStatus(currentPollCount + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, maxPolls, pollInterval]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus(0);
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/4450nlnf_abracadabait_logo_transparent.png" 
              alt="AbracadaBati"
              className="h-12 w-auto mx-auto"
            />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Loading / Polling */}
          {(status === 'loading' || status === 'polling') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Loader2 size={40} className="text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Vérification en cours...
              </h1>
              <p className="text-gray-600">
                Nous confirmons ton paiement avec Stripe
              </p>
              {status === 'polling' && (
                <p className="text-sm text-gray-400 mt-4">
                  Tentative {pollCount}/{maxPolls}
                </p>
              )}
            </motion.div>
          )}

          {/* Success */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Paiement confirmé !
              </h1>
              
              <p className="text-gray-600 mb-6">
                {paymentData?.payment_status === 'free_trial' 
                  ? "Votre période d'essai gratuite est activée !"
                  : "Merci pour ta confiance. Ton abonnement est maintenant actif."
                }
              </p>

              {/* Confetti effect */}
              <div className="flex justify-center gap-2 mb-6">
                <Sparkles className="text-yellow-500 animate-pulse" />
                <Sparkles className="text-orange-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
                <Sparkles className="text-yellow-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>

              {paymentData && (
                <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                  <h3 className="font-medium text-gray-900 mb-2">Récapitulatif</h3>
                  <div className="space-y-1 text-sm">
                    {paymentData.plan_id && (
                      <p className="text-gray-600">
                        Plan : <span className="font-medium text-gray-900 capitalize">{paymentData.plan_id}</span>
                      </p>
                    )}
                    <p className="text-gray-600">
                      Montant : <span className="font-medium text-gray-900">
                        {paymentData.amount === 0 ? 'Gratuit (1 an)' : `${paymentData.amount}€`}
                      </span>
                    </p>
                    <p className="text-gray-600">
                      Session : <span className="font-mono text-xs text-gray-500">{sessionId?.slice(0, 20)}...</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/pro')}
                  className="w-full h-12 bg-green-600 hover:bg-green-700"
                >
                  Accéder à mon Espace Pro
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Retour à l'accueil
                </Button>
              </div>
            </motion.div>
          )}

          {/* Error */}
          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle size={40} className="text-red-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Une erreur est survenue
              </h1>
              
              <p className="text-gray-600 mb-6">
                Nous n'avons pas pu confirmer ton paiement. 
                Si tu as été débité, contacte notre support.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setPollCount(0);
                    setStatus('loading');
                    pollPaymentStatus(0);
                  }}
                  className="w-full h-12"
                >
                  Réessayer
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/contact')}
                  className="w-full"
                >
                  Contacter le support
                </Button>
              </div>
            </motion.div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Paiement sécurisé par Stripe
        </p>
      </motion.div>
    </div>
  );
}
